import { useRef } from 'react';
import { Dispatch, SetStateAction, useEffect, useState  } from 'react';
import { EventInput, DateSelectArg, EventClickArg, EventDropArg, EventChangeArg, DatesSetArg } from "@fullcalendar/core";
import { API } from 'helpers/ApiHelpers';
import { Code, Form, Utility } from 'helpers/FormImports';
import { Appointment } from 'models/AppointmentModel';
import { MessageType } from 'models/BaseModel';
import { CustomSchedule } from 'models/CustomSchedule';



const openClientFormAsPopup = (
    selectedEvent: EventInput | undefined,
    setShowForm: Dispatch<SetStateAction<boolean>>,
    setFormData: Dispatch<SetStateAction<Appointment>>,
    setLoading: (loading: boolean) => void,
    setError: (error: string) => void
  ) => {
    if(selectedEvent) {
      const keyId = selectedEvent.event.id;
      API.Data.GetByKeyId<Appointment>(Code.Form.Name.APPOINTMENT, keyId, setLoading, setError)
        .then(async(data) => { if(data) {
          const clientData = await API.Data.getClientNameNumber(data.client_id);
          const formData = await Form.Map.Data(data);
          setFormData((prevData) => ({
            ...prevData,
            ...formData,
            ...clientData, 
          }));
          //await new Promise((resolve) => setTimeout(resolve, 1000));
        }})
      setShowForm(true);
    }
    
};

 // Handle event change like drag/drop or resize
const handleEventChange = (
    eventInfo: EventChangeArg,
    setMessage: Dispatch<SetStateAction<MessageType>>
  ) => {
    SaveAppointmentChanges(eventInfo, setMessage);
    Utility.Message.Info(["Appointment has been updated."],setMessage);
};


// Handle selecting dates to create new events
const handleEventAdd = (
    eventInfo: DateSelectArg,
    setShowForm: Dispatch<SetStateAction<boolean>>,
    setFormData: Dispatch<SetStateAction<Appointment>>,
    setSelectedEvent: Dispatch<SetStateAction<EventInput | undefined>>,
    doctorId: string | null,
    branchId: string | null
  ) => {
    setFormData((prev): Appointment => ({
      ...prev,
      appointment_key: Code.Key.NEW,
      appointment_start_date: eventInfo.start,
      appointment_start_time: Utility.Format.Date(eventInfo.start, Code.Format.DateTime.TimeSS),
      appointment_end_date: eventInfo.end,
      appointment_end_time: Utility.Format.Date(eventInfo.end, Code.Format.DateTime.TimeSS),
      appointment_comment: "",
      client_id: 0,
      doctor_id: Number(doctorId) || 0,
      branch_id: Number(branchId) || 0,
      type_id: 2,
      status_id: 5, //new entry
      postcheck_id: 2,
    }));
    setSelectedEvent(undefined);
    setShowForm(true);
  };

function mapAppointmentsToEvent(appointmentData: any[]): EventInput[] {
  console.log("Mapping---------------:", appointmentData);
  return appointmentData.map((item) => ({
    id: item.appointment_key,
    title: `${item.client_first_name} ${item.client_middle_name} ${item.client_last_name} ${Utility.Format.Gender(item.gender_id)} ${Utility.Format.AgeYear(item.client_dob)} ${Utility.Format.NewOld(item.newold_id)}`,
    start: `${item.appointment_start_date.split('T')[0]}T${item.appointment_start_time}`,
    end: `${item.appointment_end_date.split('T')[0]}T${item.appointment_end_time}`,
    extendedProps: {
      clientId: item.client_id,
      typeName: item.type_name,
      statusColor: item.status_color,
      clientGender: item.gender_id,
      clientDob: item.client_dob,
      info: GetEventInfo({
        firstName: item.client_first_name,
        middleName: item.client_middle_name,
        lastName: item.client_last_name,
        purpose: item.type_name,
        status: item.status_name,
        postCheck: item.postcheck_name,
        doctorName: item.doctor_first_name,
        branchName: item.branch_name,
        clientPhone: item.client_phone,
        staffName: item.user_first_name,
        remarks: item.appointment_comment
      })
    }
  }));
}

function mapScheduleToEvent(data: CustomSchedule[] | CustomSchedule[][], branchId: number): EventInput[] {
  // flatten if nested array
  const flatData = Array.isArray(data[0]) ? (data as CustomSchedule[][]).flat() : (data as CustomSchedule[]);
  
  return flatData.map(item => ({
    id: String(item.schedule_id),
    title: item.doctor_short_name,
    start: item.schedule_start_date,
    end: item.schedule_start_date,
    extendedProps: {
      keyId: item.schedule_key,
      branchId: branchId,
      doctorId: item.doctor_id,
      doctorName: (`${item.doctor_first_name || ''} ${item.doctor_middle_name} ${item.doctor_last_name}`).replace(/  /g," ").trim(),
      doctorColor: item.doctor_color,
      startTime: item.schedule_start_time,
      endTime: item.schedule_end_time,
      total: `${item.appointment_count}`,
    },
  }));
}

function getScheduleToEvent(data: CustomSchedule[] | CustomSchedule[][]): EventInput[] {
    // flatten if nested array
    const flatData = Array.isArray(data[0]) ? (data as CustomSchedule[][]).flat() : (data as CustomSchedule[]);
    
    return flatData.map(item => ({
      // id: String(item.schedule_id),
      // title: (`${item.doctor_first_name || ''} ${item.doctor_middle_name} ${item.doctor_last_name}`).replace(/  /g," ").trim(),
      start: item.schedule_start_date,
      startTime: item.schedule_start_time || "10:00:00",
      endTime: item.schedule_end_time || "18:00:00",
      display: "background",
      color: "rgba(0, 128, 255, 0.4)",
    }));
  }

function getScheduleConstraint(data: CustomSchedule[] | CustomSchedule[][]): { startTime: string; endTime: string } | null {
  // Flatten if nested array
  const flatData = Array.isArray(data[0]) ? (data as CustomSchedule[][]).flat() : (data as CustomSchedule[]);

  if (!flatData.length) return null;

  // Take the first schedule entry (assuming same working hours)
  const first = flatData[0];

  return {
    startTime: first.schedule_start_time || "10:00:00",
    endTime: first.schedule_end_time || "18:00:00",
  };
}


function GetEventInfo(data: {
  firstName: string;
  middleName: string;
  lastName: string;
  purpose: string;
  status: string;
  postCheck: string;
  doctorName: string;
  branchName: string;
  clientPhone: string;
  staffName: string;
  remarks: string;
}): string {
  const formattedPhone = data.clientPhone
    .replace(/\D/g, "")
    .replace(/^(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3");

  return `
    <div class='fc-event-info'>
      <b>${data.firstName} ${data.middleName} ${data.lastName}</b>
      <div><p>Purpose</p><span>${data.purpose}</span></div>
      <div><p>Status</p><span>${data.status}</span></div>
      <div><p>Check</p><span>${data.postCheck}</span></div>
      <div><p>Doctor</p><span>${data.doctorName}</span></div>
      <div><p>Location</p><span>${data.branchName}</span></div>
      <div><p>Phone</p><span>${formattedPhone}</span></div>
      <div><p>Staff</p><span>${data.staffName}</span></div>
      <div><p>Comment</p><span>${data.remarks}</span></div>
    </div>
  `;
}

function SaveAppointmentChanges(
  eventInfo: EventChangeArg, 
  setMessage: Dispatch<SetStateAction<MessageType>>
): void {
  const keyId = eventInfo.event.id;
  const start = new Date(eventInfo.event.start!);
  const end = new Date(eventInfo.event.end!);

  const finalData = {
    appointment_start_date: Utility.Format.Date(start, Code.Format.DateTime.Date),
    appointment_start_time: Utility.Format.Date(start, Code.Format.DateTime.TimeSS),
    appointment_end_date: Utility.Format.Date(end, Code.Format.DateTime.Date),
    appointment_end_time: Utility.Format.Date(end, Code.Format.DateTime.TimeSS),
  };

  API.Data.Save(Code.Form.Name.APPOINTMENT, keyId, finalData, setMessage, true, Code.Form.PopupClose.NONE);

}


const useDraggableForm = (
  formRef: React.RefObject<HTMLDivElement>,
  enabled: boolean 
) => {
  useEffect(() => {
    if (!enabled) return;

    const form = formRef.current;
    const header = form?.querySelector('.fc-form-header') as HTMLElement | null;

    if (!form || !header) return;

    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;

      const rect = form.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

      form.style.left = `${rect.left + scrollLeft}px`;
      form.style.top = `${rect.top + scrollTop}px`;
      form.style.position = 'absolute';
      form.style.transform = 'none';
      form.style.margin = '0';
      form.style.transition = 'none';

      offsetX = e.clientX - rect.left;
      offsetY = e.clientY - rect.top;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      form.style.left = `${e.clientX - offsetX}px`;
      form.style.top = `${e.clientY - offsetY}px`;
    };

    const onMouseUp = () => {
      isDragging = false;
    };

    header.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      header.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [formRef, enabled]);
};

function useClickChecker() {
  const clickTimeout = useRef<NodeJS.Timeout | null>(null);

  const checkHowClicked = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
        clickTimeout.current = null;
        resolve(true); // Double click
      } else {
        clickTimeout.current = setTimeout(() => {
          clickTimeout.current = null;
          resolve(false); // Single click
        }, 300);
      }
    });
  };

  return { checkHowClicked };
}

const GetChangeDate = (
    arg: DatesSetArg,
    setStartDate: (startDate: string) => void,
    setEndDate: (endDate: string) => void,
  ): void => {
    const start = Utility.Format.Date(arg.start, Code.Format.DateTime.Date);
    const end = Utility.Format.Date(Utility.Get.CorrectEndDate(arg.end), Code.Format.DateTime.Date);
    setStartDate(start)
    setEndDate(end)
  };

const setDateRange = (
  viewType: string | null,
  startDate: string | null,
  endDate: string | null,
  calendarRef: React.RefObject<any>,
  setStartDate: (startDate: string) => void,
  setEndDate: (endDate: string) => void
): void => {

  if(viewType === Code.Calendar.View.DayGrid && startDate) {
      setStartDate(startDate); 
      if(endDate) { setEndDate(endDate); } 
      else { setEndDate(startDate); }
  }
  else
  {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const view = calendarApi.view;
      const start = Utility.Format.Date(view.activeStart, Code.Format.DateTime.Date);
      const end = Utility.Format.Date(Utility.Get.CorrectEndDate(view.activeEnd), Code.Format.DateTime.Date);
      setStartDate(start);
      setEndDate(end);
    } 
  }
};

const openClientFormOnPanel = (title: string, clientId: string) => {
    if (window.parent.parent && typeof window.parent.parent.openForm === "function") {
      window.parent?.parent?.openForm?.(title, `client/${clientId}` )?.();
    }
  };


export const CalendarHelper = {
    openClientFormOnPopup: openClientFormAsPopup,
    openClientFormOnPanel: openClientFormOnPanel,
    handleEventChange: handleEventChange,
    handleEventAdd: handleEventAdd,
    mapAppointmentsToEvent: mapAppointmentsToEvent,
    mapScheduleToEvent: mapScheduleToEvent,
    getScheduleToEvent: getScheduleToEvent,
    getScheduleConstraint: getScheduleConstraint,
    DraggableForm: useDraggableForm,
    ClickChecker: useClickChecker,
    ChangeDate: GetChangeDate,
    setDateRange: setDateRange,
}

